import type * as THREE from "three";
import type { DecorModel } from "../../core/decorModels";
import type { FlightProfile } from "../../core/decorFlights";
import { buildOpenEscalator, DEFAULT_ESCALATOR_RISE, openEscalatorLength, openEscalatorProfile } from "./proceduralEscalator";
import { buildOpenStairs, openStairsLength, openStairsProfile } from "./proceduralOpenStairs";
import { buildSolidStairs, solidStairsLength, solidStairsProfile } from "./proceduralSolidStairs";

const STAIRS_STEP_RISE = 0.17;
const STAIRS_STEP_TREAD = 0.3;

/** Pente d'un escalier aux marches standard, celle d'un élément qui n'en fixe pas (`ShapeDecor.slopeDeg`). */
export const DEFAULT_STAIRS_SLOPE_DEG = (Math.atan(STAIRS_STEP_RISE / STAIRS_STEP_TREAD) * 180) / Math.PI;
export const MIN_STAIRS_SLOPE_DEG = 15;
export const MAX_STAIRS_SLOPE_DEG = 50;

/** Un escalier ou un escalator posé à l'air libre, dont la longueur découle du dénivelé. */
export function isFreeFlight(model: DecorModel): boolean {
  return model.procedural === "escalator-open"
    || model.procedural === "stairs-open"
    || model.procedural === "stairs-solid"
    || model.procedural === "stairs-solid-landing";
}

/** Une volée libre dont on règle la pente : un escalier ; celle d'un escalator est fixe. */
export function flightHasSlope(model: DecorModel): boolean {
  return isFreeFlight(model) && model.procedural !== "escalator-open";
}

export function defaultFlightRise(model: DecorModel): number {
  return model.rise ?? DEFAULT_ESCALATOR_RISE;
}

/** Profondeur des marches pour cette pente ; sans pente, celle des marches standard. */
function treadFor(slopeDeg: number | undefined): number | undefined {
  if (slopeDeg === undefined) return undefined;
  const slope = Math.min(MAX_STAIRS_SLOPE_DEG, Math.max(MIN_STAIRS_SLOPE_DEG, slopeDeg));
  return STAIRS_STEP_RISE / Math.tan((slope * Math.PI) / 180);
}

/** Longueur au sol d'une volée libre de ce dénivelé, paliers compris. */
export function freeFlightLength(model: DecorModel, rise: number, slopeDeg?: number): number {
  if (model.procedural === "escalator-open") return openEscalatorLength(rise);
  if (model.procedural === "stairs-open") return openStairsLength(rise, treadFor(slopeDeg));
  return solidStairsLength(rise, model.procedural === "stairs-solid-landing", treadFor(slopeDeg));
}

export function freeFlightProfile(model: DecorModel, rise: number, slopeDeg?: number): FlightProfile {
  if (model.procedural === "escalator-open") return openEscalatorProfile(rise);
  if (model.procedural === "stairs-open") return openStairsProfile(rise, treadFor(slopeDeg));
  return solidStairsProfile(rise, model.procedural === "stairs-solid-landing", treadFor(slopeDeg));
}

export function buildFreeFlight(model: DecorModel, width: number, rise: number, slopeDeg?: number): THREE.Group {
  if (model.procedural === "escalator-open") return buildOpenEscalator(width, rise);
  if (model.procedural === "stairs-open") return buildOpenStairs(width, rise, treadFor(slopeDeg));
  return buildSolidStairs(width, rise, {
    landing: model.procedural === "stairs-solid-landing",
    finish: model.finish ?? "concrete",
    tread: treadFor(slopeDeg),
  });
}
